<template>
  <div>
    <transition name="login-dialog-fade">
      <div v-if="visible" class="login-dialog" @click.self="handleClose">
        <div class="panel" :class="{
          'is-register': isRegister,
          'is-forgot-password': isForgotPassword,
        }">
          <button type="button" class="close" aria-label="关闭登录注册弹窗" @click="handleClose" />

          <template v-if="isRegister">
            <h3 class="title">立即注册</h3>
          </template>
          <template v-else-if="isForgotPassword">
            <h3 class="title">忘记密码</h3>
          </template>
          <template v-else>
            <div class="tabs" role="tablist" aria-label="登录方式切换">
              <button type="button" class="tab" :class="{ 'is-active': mode === 'password' }"
                @click="switchMode('password')">
                密码登录
              </button>
              <button type="button" class="tab" :class="{ 'is-active': mode === 'code' }" @click="switchMode('code')">
                验证码登录
              </button>
            </div>

            <div class="tab-track">
              <span class="tab-indicator" :style="tabIndicatorStyle" />
            </div>
          </template>

          <label class="field">
            <span class="field-icon field-icon-phone" />
            <input v-model.trim="formData.phone" type="text" class="input" placeholder="请输入手机号" />
          </label>

          <label v-if="mode === 'password'" class="field field-password">
            <span class="field-icon field-icon-password" />
            <input v-model.trim="formData.password" type="password" class="input" placeholder="请输入密码" />
          </label>

          <label v-if="isForgotPassword" class="field field-password">
            <span class="field-icon field-icon-password" />
            <input v-model.trim="formData.password" type="password" class="input" placeholder="请输入新密码" />
          </label>

          <label v-if="mode === 'code' || isRegister || isForgotPassword" class="field field-code">
            <span class="field-icon field-icon-code" />
            <input v-model.trim="formData.code" type="text" class="input" placeholder="请输入手机验证码" />
            <button type="button" class="code-btn" :disabled="sendingCode || codeCountdown > 0" @click="handleSendCode">
              {{ sendingCode ? "发送中..." : (codeCountdown > 0 ? `${codeCountdown}秒后重试` : "获取验证码") }}
            </button>
          </label>

          <label v-if="isRegister" class="field">
            <span class="field-icon field-icon-password" />
            <input v-model.trim="formData.registerPassword" type="password" class="input"
              placeholder="请设置登录密码6-15位数字字母标点组合" />
          </label>

          <button type="button" class="submit" :disabled="submitting" @click="handleSubmit">
            {{ submitting ? "提交中..." : submitText }}
          </button>

          <div v-if="mode === 'password'" class="footer footer-spread">
            <button type="button" class="text-btn" @click="switchMode('forgot-password')">
              忘记密码
            </button>
            <p class="hint">
              <span class="hint-muted">您还没有账号，</span>
              <button type="button" class="text-btn text-btn-accent" @click="switchMode('register')">
                立即注册
              </button>
            </p>
          </div>

          <div v-if="mode === 'code'" class="footer footer-end">
            <p class="hint">
              <span class="hint-muted">您还没有账号，</span>
              <button type="button" class="text-btn text-btn-accent" @click="switchMode('register')">
                立即注册
              </button>
            </p>
          </div>

          <div v-if="isRegister" class="agreement">
            <button type="button" class="checkbox" :class="{ 'is-checked': formData.agreed }"
              aria-label="切换是否同意用户协议和隐私政策" @click="toggleAgreement">
              <span />
            </button>
            <p class="agreement-text">
              注册即表示同意
              <button type="button" class="text-btn text-btn-accent agreement-link" :disabled="agreementLoading"
                @click="openRichTextDialog('agreement')">
                《用户协议》
              </button>
              和
              <button type="button" class="text-btn text-btn-accent agreement-link" :disabled="agreementLoading"
                @click="openRichTextDialog('privacy')">
                《隐私政策》
              </button>
            </p>
          </div>

          <div v-if="isRegister" class="footer footer-center">
            <p class="hint">
              <span class="hint-muted">您已有账号，</span>
              <button type="button" class="text-btn text-btn-accent" @click="switchMode('password')">
                立即登录
              </button>
            </p>
          </div>

          <div v-if="isForgotPassword" class="footer footer-center footer-back-login">
            <p class="hint">
              <span class="hint-muted">想起密码了，</span>
              <button type="button" class="text-btn text-btn-accent" @click="switchMode('password')">
                返回登录
              </button>
            </p>
          </div>
        </div>
      </div>
    </transition>

    <transition name="agreement-dialog-fade">
      <div v-if="agreementDialogVisible" class="agreement-dialog" @click.self="closeRichTextDialog">
        <div class="agreement-panel">
          <button type="button" class="agreement-close" aria-label="关闭协议弹窗" @click="closeRichTextDialog" />

          <h3 class="agreement-title">{{ agreementDialogTitle }}</h3>

          <div class="agreement-body">
            <div class="agreement-rich-text" v-html="agreementDialogContent" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { setToken } from "@/util/token-util.js";

const createDefaultFormData = () => ({
  phone: "",
  password: "",
  code: "",
  registerPassword: "",
  agreed: false,
});

const AGREEMENT_DIALOG_MAP = {
  agreement: {
    id: 120,
    title: "用户协议",
  },
  privacy: {
    id: 109,
    title: "隐私政策",
  },
};

function getPasswordRuleMessage(label = "密码") {
  return `${label}需为6-15位，且由字母、数字、标点中至少两种组合`;
}

function isPasswordValid(value) {
  const password = String(value || "");

  if (!/^[\x21-\x7e]{6,15}$/.test(password)) {
    return false;
  }

  let typeCount = 0;

  if (/[A-Za-z]/.test(password)) {
    typeCount += 1;
  }

  if (/\d/.test(password)) {
    typeCount += 1;
  }

  if (/[^A-Za-z\d]/.test(password)) {
    typeCount += 1;
  }

  return typeCount >= 2;
}

export default {
  name: "LoginDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mode: "password",
      formData: createDefaultFormData(),
      sendingCode: false,
      codeCountdown: 0,
      submitting: false,
      agreementLoading: false,
      agreementDialogVisible: false,
      agreementDialogTitle: "",
      agreementDialogContent: "",
    };
  },
  computed: {
    isRegister() {
      return this.mode === "register";
    },
    isForgotPassword() {
      return this.mode === "forgot-password";
    },
    submitText() {
      if (this.isRegister) {
        return "立即注册";
      }

      if (this.isForgotPassword) {
        return "确认修改";
      }

      return "登录";
    },
    tabIndicatorStyle() {
      return this.mode === "code"
        ? {
          width: "80px",
          transform: "translateX(203px)",
        }
        : {
          width: "63px",
          transform: "translateX(67px)",
        };
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.resetDialog();
      }
    },
  },
  methods: {
    resetDialog() {
      this.mode = "password";
      this.formData = createDefaultFormData();
      this.codeCountdown = 0;
    },
    switchMode(mode) {
      const needsReset = ["register", "forgot-password"].includes(mode) || ["register", "forgot-password"].includes(this.mode);
      this.mode = mode;
      if (needsReset) {
        this.formData = createDefaultFormData();
        this.codeCountdown = 0;
      }
    },
    toggleAgreement() {
      this.formData.agreed = !this.formData.agreed;
    },
    async openRichTextDialog(type) {
      const dialogConfig = AGREEMENT_DIALOG_MAP[type];

      if (!dialogConfig) {
        return;
      }

      this.agreementLoading = true;

      try {
        const res = await this.$api({
          url: "Txt/show",
          method: "get",
          data: {
            id: dialogConfig.id,
            lang: "zh",
          },
        });

        if (res.code !== 200) {
          this.$message.error(res.msg || `${dialogConfig.title}获取失败`);
          return;
        }

        const content = (res.data && res.data.content_html) || "";

        if (!content) {
          this.$message.warning(`${dialogConfig.title}暂无内容`);
          return;
        }

        this.agreementDialogTitle = dialogConfig.title;
        this.agreementDialogContent = content;
        this.agreementDialogVisible = true;
      } catch (error) {
        this.$message.error(`${dialogConfig.title}获取失败，请稍后重试`);
      } finally {
        this.agreementLoading = false;
      }
    },
    closeRichTextDialog() {
      this.agreementDialogVisible = false;
      this.agreementDialogTitle = "";
      this.agreementDialogContent = "";
    },
    handleClose() {
      this.$emit("close");
    },
    validatePasswordField(value, label) {
      if (!value) {
        this.$message.warning(`请输入${label}`);
        return false;
      }

      if (!isPasswordValid(value)) {
        this.$message.warning(getPasswordRuleMessage(label));
        return false;
      }

      return true;
    },
    startCodeCountdown() {
      this.codeCountdown = 60;
      const timer = setInterval(() => {
        this.codeCountdown -= 1;
        if (this.codeCountdown <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    },
    async handleSendCode() {
      const phone = this.formData.phone;

      if (!phone) {
        this.$message.warning("请先输入手机号");
        return;
      }

      this.sendingCode = true;

      try {
        const res = await this.$api({
          url: "User/sendSms",
          method: "get",
          data: {
            mobile: phone,
          },
        });

        if (res.code === 200) {
          this.$message.success(res.msg || "验证码已发送");
          this.startCodeCountdown();
          return;
        }

        this.$message.error(res.msg || "验证码发送失败");
      } catch (error) {
        this.$message.error("验证码发送失败，请稍后重试");
      } finally {
        this.sendingCode = false;
      }
    },
    async handleSubmit() {
      const phone = String(this.formData.phone || "").trim();

      if (this.isForgotPassword) {
        if (!phone) {
          this.$message.warning("请输入手机号");
          return;
        }

        if (!this.formData.code) {
          this.$message.warning("请输入手机验证码");
          return;
        }

        if (!this.formData.password) {
          this.$message.warning("请输入新密码");
          return;
        }

        if (!this.validatePasswordField(this.formData.password, "新密码")) {
          return;
        }

        this.submitting = true;

        try {
          const res = await this.$api({
            url: "User/forgot",
            method: "get",
            data: {
              mobile: phone,
              code: this.formData.code,
              password: this.formData.password,
            },
          });

          if (res.code === 200) {
            this.$message.success(res.msg || "密码修改成功");
            this.handleClose();
            return;
          }

          this.$message.error(res.msg || "密码修改失败");
        } catch (error) {
          this.$message.error("密码修改失败，请稍后重试");
        } finally {
          this.submitting = false;
        }

        return;
      }

      if (!phone) {
        this.$message.warning("请输入手机号");
        return;
      }

      this.submitting = true;

      try {
        if (this.mode === "code") {
          if (!this.formData.code) {
            this.$message.warning("请输入手机验证码");
            return;
          }

          const res = await this.$api({
            url: "User/mobileCodeLogin",
            method: "get",
            data: {
              mobile: phone,
              code: this.formData.code,
            },
          });

          if (res.code == 200 && res.data && res.data.token) {
            setToken(res.data.token, true);
            localStorage.setItem("yinlv_user", JSON.stringify(res.data));
            this.$message.success(res.msg || "登录成功");
            this.$emit("login-success");
            this.handleClose();
            return;
          }

          this.$message.error(res.msg || "登录失败");
          return;
        }

        if (this.isRegister) {
          if (!this.formData.code) {
            this.$message.warning("请输入手机验证码");
            return;
          }

          if (!this.formData.registerPassword) {
            this.$message.warning("请设置登录密码");
            return;
          }

          if (
            !this.validatePasswordField(
              this.formData.registerPassword,
              "登录密码",
            )
          ) {
            return;
          }

          if (!this.formData.agreed) {
            this.$message.warning("请先勾选用户协议和隐私政策");
            return;
          }

          const registerRes = await this.$api({
            url: "User/mobileRegister",
            method: "get",
            data: {
              mobile: phone,
              password: this.formData.registerPassword,
              code: this.formData.code,
            },
          });

          if (registerRes.code !== 200) {
            this.$message.error(registerRes.msg || "注册失败");
            return;
          }

          this.$message.success(registerRes.msg || "注册成功");

          const loginRes = await this.$api({
            url: "User/mobileLogin",
            method: "get",
            data: {
              mobile: phone,
              password: this.formData.registerPassword,
            },
          });

          if (loginRes.code == 200 && loginRes.data && loginRes.data.token) {
            setToken(loginRes.data.token, true);
            localStorage.setItem("yinlv_user", JSON.stringify(loginRes.data));
            this.$message.success("登录成功");
            this.$emit("login-success");
            this.handleClose();
            return;
          }

          this.$message.warning("注册成功，请使用密码登录");
          return;
        }

        if (!this.formData.password) {
          this.$message.warning("请输入登录密码");
          return;
        }

        if (!this.validatePasswordField(this.formData.password, "登录密码")) {
          return;
        }

        const res = await this.$api({
          url: "User/mobileLogin",
          method: "get",
          data: {
            mobile: phone,
            password: this.formData.password,
          },
        });

        if (res.code == 200 && res.data && res.data.token) {
          setToken(res.data.token, true);
          localStorage.setItem("yinlv_user", JSON.stringify(res.data));
          this.$message.success(res.msg || "登录成功");
          this.$emit("login-success");
          this.handleClose();
          return;
        }

        this.$message.error(res.msg || "登录失败");
      } catch (error) {
        this.$message.error("登录失败，请稍后重试");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.login-dialog {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  background: rgba(0, 0, 0, 0.5);
}

.panel {
  position: relative;
  box-sizing: border-box;
  width: 380px;
  height: 366px;
  padding: 15px 20px 41px 23px;
  background: #ffffff;
  box-shadow: 0 22px 48px rgba(0, 0, 0, 0.24);
}

.panel.is-register {
  width: 383px;
  height: 454px;
  padding-right: 23px;
  padding-bottom: 39px;
}

.panel.is-forgot-password {
  height: 380px;
}

.close {
  position: absolute;
  top: 15px;
  right: 20px;
  width: 14px;
  height: 14px;
  border: 0;
  padding: 0;
  background: url("../../../assets/home-confirm/dialogs/close.svg") center / 14px 14px no-repeat;
  cursor: pointer;
}

.title {
  margin: 13px 0 29px;
  color: #000000;
  font-size: 16px;
  font-weight: 700;
  line-height: 19px;
  text-align: center;
}

.tabs {
  display: flex;
  justify-content: center;
  column-gap: 73px;
  margin: 13px 0 15px;
}

.tab {
  border: 0;
  padding: 0;
  background: transparent;
  color: #000000;
  font-size: 16px;
  line-height: 22px;
  cursor: pointer;
}

.tab.is-active {
  font-weight: 700;
}

.tab-track {
  position: relative;
  width: 333px;
  height: 1px;
  margin-left: 1px;
  background: #dedede;
}

.tab-indicator {
  position: absolute;
  top: -2px;
  left: 0;
  display: block;
  height: 3px;
  background: #d92f24;
  transition: transform 0.2s ease;
}

.field {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  margin-top: 21px;
  border: 1px solid #dedede;
  border-radius: 4px;
  padding: 0 11px;
}

.tabs+.tab-track+.field {
  margin-top: 38px;
}

.title+.field {
  margin-top: 0;
}

.field-icon {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin-right: 12px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
}

.field-icon-phone {
  background-image: url("../../../assets/home-confirm/dialogs/phone.svg");
}

.field-icon-password,
.field-icon-code {
  background-image: url("../../../assets/home-confirm/dialogs/verify-code.svg");
}

.input {
  flex: 1;
  width: 100%;
  border: 0;
  padding: 0;
  color: #333333;
  font-size: 16px;
  line-height: 22px;
  outline: none;
}

.input::placeholder {
  color: #999999;
}

.field-code {
  padding-right: 13px;
}

.field-password .field-icon {
  width: 16px;
}

.code-btn {
  flex: 0 0 auto;
  border: 0;
  padding: 0;
  background: transparent;
  color: #d92f24;
  font-size: 16px;
  line-height: 22px;
  cursor: pointer;
}

.code-btn[disabled],
.submit[disabled] {
  cursor: not-allowed;
  opacity: 0.6;
}

.submit {
  width: 100%;
  height: 40px;
  margin-top: 31px;
  border: 0;
  border-radius: 4px;
  background: #d92f24;
  color: #ffffff;
  font-size: 16px;
  line-height: 22px;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease;
}

.submit:hover {
  filter: brightness(1.04);
  transform: translateY(-1px);
}

.footer {
  display: flex;
  align-items: center;
  margin-top: 13px;
}

.footer-spread {
  justify-content: space-between;
}

.footer-end {
  justify-content: flex-end;
}

.footer-center {
  justify-content: center;
  margin-top: 31px;
}

.footer-back-login {
  margin-top: 20px;
}

.hint {
  display: flex;
  align-items: center;
  margin: 0;
  color: #000000;
  font-size: 16px;
  line-height: 22px;
}

.hint-muted {
  color: #999999;
}

.text-btn {
  border: 0;
  padding: 0;
  background: transparent;
  color: #999999;
  font-size: 16px;
  line-height: 22px;
  cursor: pointer;
}

.text-btn-accent {
  color: #d92f24;
}

.agreement {
  display: flex;
  align-items: center;
  column-gap: 14px;
  margin-top: 23px;
}

.checkbox {
  position: relative;
  flex: 0 0 auto;
  width: 23px;
  height: 23px;
  border: 1px solid #dedede;
  border-radius: 2px;
  padding: 0;
  background: #ffffff;
  cursor: pointer;
}

.checkbox span {
  position: absolute;
  top: 5px;
  left: 7px;
  width: 7px;
  height: 11px;
  border-right: 2px solid transparent;
  border-bottom: 2px solid transparent;
  transform: rotate(40deg);
}

.checkbox.is-checked {
  border-color: #d92f24;
  background: #d92f24;
}

.checkbox.is-checked span {
  border-color: #ffffff;
}

.agreement-text {
  margin: 0;
  color: #000000;
  font-size: 14px;
  line-height: 20px;
}

.agreement-link {
  font-size: 14px;
  line-height: 20px;
}

.agreement-dialog {
  position: fixed;
  inset: 0;
  z-index: 2001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  background: rgba(0, 0, 0, 0.5);
}

.agreement-panel {
  position: relative;
  box-sizing: border-box;
  width: 960px;
  max-width: 100%;
  padding: 22px 28px 28px;
  background: #ffffff;
  box-shadow: 0 22px 48px rgba(0, 0, 0, 0.24);
}

.agreement-close {
  position: absolute;
  top: 18px;
  right: 20px;
  width: 14px;
  height: 14px;
  border: 0;
  padding: 0;
  background: url("../../../assets/home-confirm/dialogs/close.svg") center / 14px 14px no-repeat;
  cursor: pointer;
}

.agreement-title {
  margin: 0;
  color: #000000;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  text-align: center;
}

.agreement-body {
  margin-top: 20px;
  padding: 24px 26px;
  max-height: 70vh;
  overflow-y: auto;
  background: #ffffff;
}

.login-dialog-fade-enter-active,
.login-dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.login-dialog-fade-enter-active .panel,
.login-dialog-fade-leave-active .panel {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.login-dialog-fade-enter,
.login-dialog-fade-leave-to {
  opacity: 0;
}

.login-dialog-fade-enter .panel,
.login-dialog-fade-leave-to .panel {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}

.agreement-dialog-fade-enter-active,
.agreement-dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.agreement-dialog-fade-enter-active .agreement-panel,
.agreement-dialog-fade-leave-active .agreement-panel {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.agreement-dialog-fade-enter,
.agreement-dialog-fade-leave-to {
  opacity: 0;
}

.agreement-dialog-fade-enter .agreement-panel,
.agreement-dialog-fade-leave-to .agreement-panel {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}
</style>
<style scoped src="./LoginDialog.mobile.css"></style>
